import { MarketingTrending } from '../../../domain/marketing-trending';
import {
  CreateMarketingTrendingPageItemRequestApiModel,
  GenericLayoutApiModel,
  MarketingTrendingPageItemApiModel,
} from '@cxb/editor-stb-api';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import { MarketingItemLayout } from '../../../domain/domain-layouts';
import {
  buttonMapperDomainToApi,
  descriptionLocalizedMapperDomainToApi,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
  titleLocalizedFromHeadlineMapper,
} from '../../mapping/create-tray-domain-to-api';
import {
  buttonMapperApiToDomain,
  commonMapper,
  descriptionLocalizedMapperApiToDomain,
  headlineTextMapper,
  mapApiModelImageToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { MarketingTrendingTrayFormViewModel } from './marketing-trending-tray-form.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import { SchedulingType } from '../../../shared/enums/scheduling-type.enum';
import { TraySelectorType } from '../tray-selector/tray-selector.enum';
import {
  mapDomainButtonToButtonViewModel,
  mapLocalizedDomainImagesToPlatformLocalizedViewModelImage,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  mapMarketingButtonToDomainButton,
  mapViewModelImagesToLocalizedDomainImagesWithoutPlatforms,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'MarketingTrending'

/**
 * DOMAIN -> API
 */

export const mapDomainTrendingToApi = (
  trendingTray: MarketingTrending,
  pageVersionId: string,
  tenantId?: string
): CreateMarketingTrendingPageItemRequestApiModel => {
  const commonProperties = mapperPipe(
    trendingTray,
    targetingMapperDomainToApi,
    scheduledDateMapperDomainToApi,
    titleLocalizedFromHeadlineMapper,
    descriptionLocalizedMapperDomainToApi,
    buttonMapperDomainToApi
  );

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    name: trendingTray.titleEnglish,
    layout: trendingTray.template === MarketingItemLayout.TRENDING_LEFT ? GenericLayoutApiModel.Left : GenericLayoutApiModel.Right,
    contentImages: trendingTray.contentImages.map((it) => ({
      order: it.order,
      enImageId: it.en[0].id,
      arImageId: it.ar[0].id,
    })),
    tenantId,
    platformIds: trendingTray.platformIds,
  }));
};

/**
 * API -> DOMAIN
 */

export const mapApiTrendingToDomain = (trending: MarketingTrendingPageItemApiModel): MarketingTrending => {
  return remainderMapper(
    mapperPipe(
      trending,
      commonMapper,
      targetingMapperApiToDomain,
      scheduledDateMapperApiToDomain,
      buttonMapperApiToDomain,
      headlineTextMapper,
      descriptionLocalizedMapperApiToDomain
    ),
    () => ({
      type: 'MarketingTrending',
      titleEnglish: trending.name,
      template: trending.layout === GenericLayoutApiModel.Left ? MarketingItemLayout.TRENDING_LEFT : MarketingItemLayout.TRENDING_RIGHT,
      contentImages: trending.contentImages.map((it, idx) => ({
        order: idx,
        en: [
          {
            ...mapApiModelImageToDomain(it.en),
            platformName: PlatformType.Web,
          },
        ],
        ar: [
          {
            ...mapApiModelImageToDomain(it.ar),
            platformName: PlatformType.Web,
          },
        ],
      })),
    })
  );
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapDomainMarketingTrendingToViewModel = (trending: MarketingTrending): MarketingTrendingTrayFormViewModel => {
  return {
    id: trending.id,
    type: TrayFormViewModelType.TRENDING,
    countryIds: trending.countryIds,
    description: { en: trending.descriptionTextEnglish, ar: trending.descriptionTextArabic },
    headlineText: { en: trending.headlineTextEnglish, ar: trending.headlineTextArabic },
    button: mapDomainButtonToButtonViewModel(trending.primaryButton),
    scheduling: {
      schedulingType: trending.scheduledFrom || trending.scheduledTo ? SchedulingType.Schedule : SchedulingType.LiveWhenPublished,
      scheduledFrom: trending.scheduledFrom ? new Date(trending.scheduledFrom).toISOString() : undefined,
      scheduledTo: trending.scheduledTo ? new Date(trending.scheduledTo).toISOString() : undefined,
    },
    moduleName: trending.titleEnglish,
    userSegmentIds: trending.userSegmentIds,
    template:
      trending.template === MarketingItemLayout.TRENDING_LEFT
        ? TraySelectorType.MarketingTrendingLeft
        : TraySelectorType.MarketingTrendingRight,
    trendingImages: trending.contentImages.map((image) => mapLocalizedDomainImagesToPlatformLocalizedViewModelImage(image)),
  };
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapViewModelTrendingToDomain = (
  marketingTrendingViewModel: MarketingTrendingTrayFormViewModel,
  platformIds: Record<PlatformType, string>
): MarketingTrending => {
  return {
    id: marketingTrendingViewModel.id,
    scheduledFrom: marketingTrendingViewModel.scheduling?.scheduledFrom
      ? new Date(marketingTrendingViewModel.scheduling.scheduledFrom).getTime()
      : undefined,
    scheduledTo: marketingTrendingViewModel.scheduling?.scheduledTo
      ? new Date(marketingTrendingViewModel.scheduling.scheduledTo).getTime()
      : undefined,
    titleEnglish: marketingTrendingViewModel.moduleName,
    userSegmentIds: marketingTrendingViewModel.userSegmentIds,
    countryIds: marketingTrendingViewModel.countryIds,
    type: 'MarketingTrending',
    order: undefined,
    visible: undefined,
    draggable: true,
    template:
      marketingTrendingViewModel.template === TraySelectorType.MarketingTrendingLeft
        ? MarketingItemLayout.TRENDING_LEFT
        : MarketingItemLayout.TRENDING_RIGHT,
    descriptionTextArabic: marketingTrendingViewModel.description.ar,
    descriptionTextEnglish: marketingTrendingViewModel.description.en,
    headlineTextArabic: marketingTrendingViewModel.headlineText.ar,
    headlineTextEnglish: marketingTrendingViewModel.headlineText.en,
    contentImages: marketingTrendingViewModel.trendingImages.map((image, idx) =>
      mapViewModelImagesToLocalizedDomainImagesWithoutPlatforms(image, idx)
    ),
    primaryButton: mapMarketingButtonToDomainButton(marketingTrendingViewModel.button),
    platformIds: Object.values(platformIds),
  };
};
